import { useState, useRef, useMemo, useEffect } from 'react';
import { useWindowDimensions, LayoutRectangle, View, ViewStyle } from 'react-native';
import { neutrals } from 'src/theme/colors';
import { TooltipPosition, BasePressTooltipProps } from './BasePressTooltip.types';
import { ARROW_POSITION, ARROW_SIZE, MIN_DISTANCE } from './BasePressTooltip.constants';
import { useFocusContext } from 'src/contexts/FocusContext';
import { FocusGroups } from '@photosi/rn-components/focus-provider/FocusProvider.constants';

export const usePressTooltipLogic = ({
  containerStyle,
  backgroundColor = '#ffffff',
  width,
  height,
  position = TooltipPosition.BOTTOM,
  arrowSize = ARROW_SIZE,
  arrowPosition = ARROW_POSITION,
  minDistanceFromEdge = MIN_DISTANCE,
}: BasePressTooltipProps) => {
  const { width: screenWidth, height: screenHeight } = useWindowDimensions();

  const [isVisible, setIsVisible] = useState(false);
  const [triggerLayout, setTriggerLayout] = useState<LayoutRectangle | null>(null);
  const [tooltipLayout, setTooltipLayout] = useState<LayoutRectangle | null>(null);
  const { makeOnlySomeElementGroupsFocusable, makeAllElementGroupsFocusable } = useFocusContext();

  useEffect(() => {
    if (isVisible) {
      makeOnlySomeElementGroupsFocusable([FocusGroups.TOOLTIP]);
    } else {
      makeAllElementGroupsFocusable();
    }
    return () => {
      makeAllElementGroupsFocusable();
    };
  }, [isVisible]);

  const triggerRef = useRef<View>(null);

  const handlePress = () => {
    triggerRef.current?.measure((_x, _y, w, h, px, py) => {
      setTriggerLayout({ x: px, y: py, width: w, height: h });
      setIsVisible(true);
    });
  };

  const handleClose = () => {
    setIsVisible(false);
  };

  const tooltipStyle = useMemo(() => {
    const style: ViewStyle = {
      position: 'absolute',
      backgroundColor,
      borderRadius: 12,
      width,
      height,
      opacity: triggerLayout && tooltipLayout ? 1 : 0,
      shadowColor: `${neutrals.black}26`,
      shadowRadius: 40,
      shadowOffset: { width: 0, height: 0 },
    };

    if (!triggerLayout || !tooltipLayout) {
      return style;
    }

    // --- POSITIONING LOGIC ---
    let left = 0;
    let right = 0;
    let top = 0;

    switch (position) {
      case TooltipPosition.TOP:
        top = triggerLayout.y - tooltipLayout.height - arrowSize;
        left = triggerLayout.x + (triggerLayout.width - tooltipLayout.width) / 2;
        break;
      case TooltipPosition.BOTTOM:
        top = triggerLayout.y + triggerLayout.height + arrowSize;
        left = triggerLayout.x + (triggerLayout.width - tooltipLayout.width) / 2;
        break;
      case TooltipPosition.LEFT:
        top = triggerLayout.y + (triggerLayout.height - tooltipLayout.height) / 2;
        right = screenWidth - triggerLayout.x + triggerLayout.width + minDistanceFromEdge;
        break;
      case TooltipPosition.RIGHT:
        top = triggerLayout.y + (triggerLayout.height - tooltipLayout.height) / 2;
        left = triggerLayout.x + triggerLayout.width + arrowSize;
        break;
    }

    // Check for screen boundaries and adjust if necessary
    if (left < minDistanceFromEdge) {
      left = minDistanceFromEdge;
    }

    if (right < minDistanceFromEdge) {
      right = minDistanceFromEdge;
    }

    if (top < minDistanceFromEdge) {
      top = minDistanceFromEdge;
    }

    if (left + tooltipLayout.width > screenWidth - minDistanceFromEdge) {
      left = screenWidth - tooltipLayout.width - minDistanceFromEdge;
    }
    if (top + tooltipLayout.height > screenHeight - minDistanceFromEdge) {
      top = screenHeight - tooltipLayout.height - minDistanceFromEdge;
    }

    if (right + tooltipLayout.width > screenWidth - minDistanceFromEdge) {
      right = screenWidth - tooltipLayout.width - minDistanceFromEdge;
    }

    style.top = top;
    if (position === TooltipPosition.LEFT) {
      style.right = right;
    } else {
      style.left = left;
    }

    return style;
  }, [triggerLayout, tooltipLayout, backgroundColor, width, height]);

  const arrowStyle = useMemo(() => {
    const style: ViewStyle = {
      position: 'absolute',
      width: 0,
      height: 0,
      borderStyle: 'solid',
      backgroundColor: 'transparent',
    };

    if (!triggerLayout || !tooltipLayout) return style;

    switch (position) {
      case TooltipPosition.TOP:
        style.bottom = arrowPosition;
        style.left = triggerLayout.x + triggerLayout.width / 2 - (tooltipStyle.left as number)! - arrowSize;
        style.borderTopWidth = arrowSize;
        style.borderTopColor = backgroundColor;
        style.borderLeftWidth = arrowSize;
        style.borderLeftColor = 'transparent';
        style.borderRightWidth = arrowSize;
        style.borderRightColor = 'transparent';
        break;
      case TooltipPosition.BOTTOM:
        style.top = arrowPosition;
        style.left = triggerLayout.x + triggerLayout.width / 2 - (tooltipStyle.left as number)! - arrowSize;
        style.borderBottomWidth = arrowSize;
        style.borderBottomColor = backgroundColor;
        style.borderLeftWidth = arrowSize;
        style.borderLeftColor = 'transparent';
        style.borderRightWidth = arrowSize;
        style.borderRightColor = 'transparent';
        break;
      case TooltipPosition.LEFT:
        style.right = arrowPosition;
        style.top = triggerLayout.y + triggerLayout.height / 2 - (tooltipStyle.top as number)! - arrowSize;
        style.borderLeftWidth = arrowSize;
        style.borderLeftColor = backgroundColor;
        style.borderTopWidth = arrowSize;
        style.borderTopColor = 'transparent';
        style.borderBottomWidth = arrowSize;
        style.borderBottomColor = 'transparent';
        break;
      case TooltipPosition.RIGHT:
        style.left = arrowPosition;
        style.top = triggerLayout.y + triggerLayout.height / 2 - (tooltipStyle.top as number)! - arrowSize;
        style.borderRightWidth = arrowSize;
        style.borderRightColor = backgroundColor;
        style.borderTopWidth = arrowSize;
        style.borderTopColor = 'transparent';
        style.borderBottomWidth = arrowSize;
        style.borderBottomColor = 'transparent';
        break;
    }

    return style;
  }, [tooltipStyle, triggerLayout, backgroundColor]);

  return {
    triggerRef,
    handlePress,
    handleClose,
    isVisible,
    setTooltipLayout,
    tooltipStyle,
    containerStyle,
    arrowStyle,
  };
};
